/**
 * PhysicsWorld - Rapier 2D Physics Wrapper
 * 
 * Provides a clean interface to the Rapier physics engine with PPM scaling.
 * All public methods work in pixel coordinates; conversion to/from meters happens internally.
 * 
 * Body Categories:
 * - 'package': Interactive game objects (grabbable, rendered)
 * - 'environment': Static walls, conveyor (not grabbable, not rendered as packages)
 */

import RAPIER from '@dimforge/rapier2d-compat';

/** Pixels per meter - Rapier works best with meter-scale values */
export const PPM = 50;

/** Convert pixels to meters */
export function toMeters(pixels: number): number {
    return pixels / PPM;
}

/** Convert meters to pixels */
export function toPixels(meters: number): number {
    return meters * PPM;
}

/** Body type enum matching Rapier's rigid body types */
export enum BodyType {
    Dynamic = 'dynamic',
    Static = 'static',
    Kinematic = 'kinematic'
}

/** Body category for filtering */
export type BodyCategory = 'package' | 'environment';

/** Shape type for package identification */
export type ShapeType = 'box' | 'bag' | 'triangle';

/** Physics body descriptor for creating bodies */
export interface BodyDescriptor {
    type: BodyType;
    x: number;          // pixels
    y: number;          // pixels
    angle?: number;     // radians
    shape: ShapeDescriptor;
    friction?: number;
    restitution?: number;
    density?: number;
    linearDamping?: number;
    angularDamping?: number;
    ccdEnabled?: boolean;
}

/** Shape descriptor - dimensions in pixels */
export type ShapeDescriptor =
    | { type: 'box'; width: number; height: number }
    | { type: 'roundedBox'; width: number; height: number; radius: number }
    | { type: 'triangle'; radius: number }
    | { type: 'polygon'; vertices: Array<{ x: number; y: number }> };

/** User data attached to bodies for game logic */
export interface BodyUserData {
    id: number;
    category: BodyCategory;
    shapeType: ShapeType;
    width?: number;     // pixels (for boxes/bags)
    height?: number;    // pixels (for boxes/bags)
    radius?: number;    // pixels (for triangles)
    chamferRadius?: number; // pixels (for bags)
    isOnConveyor?: boolean;
    isHeld?: boolean;
}

/** Handle to a physics body */
export interface PhysicsBody {
    handle: number;
    userData: BodyUserData;
}

/**
 * PhysicsWorld - Main physics simulation wrapper
 */
export class PhysicsWorld {
    private world: RAPIER.World | null = null;
    // Only tracks PACKAGE bodies (not environment)
    private bodies: Map<number, PhysicsBody> = new Map();
    private nextBodyId = 1;

    /** Initialize the Rapier WASM module and create the world */
    async init(): Promise<void> {
        await RAPIER.init();
        
        // Create world with standard gravity (9.81 m/s² downward)
        const gravity = new RAPIER.Vector2(0, 9.81);
        this.world = new RAPIER.World(gravity);
    }

    /** Check if the world is initialized */
    isReady(): boolean {
        return this.world !== null;
    }

    /** Step the physics simulation by a fixed time step */
    step(dt: number): void {
        if (!this.world) return;
        
        // Rapier step uses seconds
        this.world.timestep = dt;
        this.world.step();
    }

    /**
     * Create a static environment body (walls, conveyor, etc.)
     * These are NOT tracked in the bodies map and cannot be grabbed.
     * Returns just the handle for reference (e.g., to remove later).
     */
    createStaticBody(desc: Omit<BodyDescriptor, 'type'>): number {
        if (!this.world) {
            throw new Error('PhysicsWorld not initialized');
        }

        const bodyDesc = RAPIER.RigidBodyDesc.fixed();
        bodyDesc.setTranslation(toMeters(desc.x), toMeters(desc.y));
        
        if (desc.angle !== undefined) {
            bodyDesc.setRotation(desc.angle);
        }

        const body = this.world.createRigidBody(bodyDesc);
        const colliderDesc = this.createColliderDesc(desc.shape);
        
        if (desc.friction !== undefined) {
            colliderDesc.setFriction(desc.friction);
        }
        if (desc.restitution !== undefined) {
            colliderDesc.setRestitution(desc.restitution);
        }

        this.world.createCollider(colliderDesc, body);

        // NOT added to this.bodies - environment bodies are not tracked
        return body.handle;
    }

    /** Create a package body (interactive, grabbable, rendered) */
    createBody(desc: BodyDescriptor): PhysicsBody {
        if (!this.world) {
            throw new Error('PhysicsWorld not initialized');
        }

        // Create rigid body description
        let bodyDesc: RAPIER.RigidBodyDesc;
        switch (desc.type) {
            case BodyType.Dynamic:
                bodyDesc = RAPIER.RigidBodyDesc.dynamic();
                break;
            case BodyType.Static:
                bodyDesc = RAPIER.RigidBodyDesc.fixed();
                break;
            case BodyType.Kinematic:
                bodyDesc = RAPIER.RigidBodyDesc.kinematicPositionBased();
                break;
        }

        // Set position in meters
        bodyDesc.setTranslation(toMeters(desc.x), toMeters(desc.y));
        
        // Set rotation if provided
        if (desc.angle !== undefined) {
            bodyDesc.setRotation(desc.angle);
        }

        // Set linear damping (air friction equivalent)
        if (desc.linearDamping !== undefined) {
            bodyDesc.setLinearDamping(desc.linearDamping);
        }

        // Set angular damping (rotational friction)
        if (desc.angularDamping !== undefined) {
            bodyDesc.setAngularDamping(desc.angularDamping);
        }

        // Enable CCD for fast-moving objects
        if (desc.ccdEnabled) {
            bodyDesc.setCcdEnabled(true);
        }

        // Create the rigid body
        const body = this.world.createRigidBody(bodyDesc);

        // Create collider based on shape
        const colliderDesc = this.createColliderDesc(desc.shape);
        
        // Set physical properties
        if (desc.friction !== undefined) {
            colliderDesc.setFriction(desc.friction);
        }
        if (desc.restitution !== undefined) {
            colliderDesc.setRestitution(desc.restitution);
        }
        if (desc.density !== undefined) {
            colliderDesc.setDensity(desc.density);
        }

        this.world.createCollider(colliderDesc, body);

        // Create user data
        const userData = this.createUserData(desc.shape);
        const id = this.nextBodyId++;
        userData.id = id;
        userData.category = 'package';

        const physicsBody: PhysicsBody = {
            handle: body.handle,
            userData
        };

        this.bodies.set(body.handle, physicsBody);

        return physicsBody;
    }

    /** Create a collider descriptor from a shape descriptor */
    private createColliderDesc(shape: ShapeDescriptor): RAPIER.ColliderDesc {
        switch (shape.type) {
            case 'box':
                // Rapier uses half-extents
                return RAPIER.ColliderDesc.cuboid(
                    toMeters(shape.width / 2),
                    toMeters(shape.height / 2)
                );

            case 'roundedBox':
                // Rapier's roundCuboid: half-extents minus border radius, then border radius
                const halfW = toMeters(shape.width / 2);
                const halfH = toMeters(shape.height / 2);
                const borderRadius = toMeters(shape.radius);
                return RAPIER.ColliderDesc.roundCuboid(
                    halfW - borderRadius,
                    halfH - borderRadius,
                    borderRadius
                );

            case 'triangle': {
                // Create equilateral triangle vertices
                const r = toMeters(shape.radius);
                const vertices = new Float32Array([
                    0, -r,                          // Top
                    r * Math.cos(Math.PI / 6), r * Math.sin(Math.PI / 6),   // Bottom right
                    -r * Math.cos(Math.PI / 6), r * Math.sin(Math.PI / 6)   // Bottom left
                ]);
                return RAPIER.ColliderDesc.convexHull(vertices) ?? RAPIER.ColliderDesc.ball(r);
            }

            case 'polygon': {
                const vertices = new Float32Array(shape.vertices.length * 2);
                for (let i = 0; i < shape.vertices.length; i++) {
                    const v = shape.vertices[i];
                    if (v === undefined) continue;
                    vertices[i * 2] = toMeters(v.x);
                    vertices[i * 2 + 1] = toMeters(v.y);
                }
                return RAPIER.ColliderDesc.convexHull(vertices) ?? RAPIER.ColliderDesc.ball(0.5);
            }
        }
    }

    /** Create user data from shape descriptor */
    private createUserData(shape: ShapeDescriptor): BodyUserData {
        switch (shape.type) {
            case 'box':
                return {
                    id: 0,
                    category: 'package',
                    shapeType: 'box',
                    width: shape.width,
                    height: shape.height
                };
            case 'roundedBox':
                return {
                    id: 0,
                    category: 'package',
                    shapeType: 'bag',
                    width: shape.width,
                    height: shape.height,
                    chamferRadius: shape.radius
                };
            case 'triangle':
                return {
                    id: 0,
                    category: 'package',
                    shapeType: 'triangle',
                    radius: shape.radius
                };
            case 'polygon':
                return {
                    id: 0,
                    category: 'package',
                    shapeType: 'box' // Custom polygons treated as boxes for now
                };
        }
    }

    /** Get body position in pixels */
    getPosition(handle: number): { x: number; y: number } | null {
        if (!this.world) return null;
        const body = this.world.getRigidBody(handle);
        if (!body) return null;
        const pos = body.translation();
        return {
            x: toPixels(pos.x),
            y: toPixels(pos.y)
        };
    }

    /** Get body rotation in radians */
    getRotation(handle: number): number | null {
        if (!this.world) return null;
        const body = this.world.getRigidBody(handle);
        if (!body) return null;
        return body.rotation();
    }

    /** Get body velocity in pixels/second */
    getVelocity(handle: number): { x: number; y: number } | null {
        if (!this.world) return null;
        const body = this.world.getRigidBody(handle);
        if (!body) return null;
        const vel = body.linvel();
        return {
            x: toPixels(vel.x),
            y: toPixels(vel.y)
        };
    }

    /** Set body position in pixels */
    setPosition(handle: number, x: number, y: number): void {
        if (!this.world) return;
        const body = this.world.getRigidBody(handle);
        if (!body) return;
        body.setTranslation(new RAPIER.Vector2(toMeters(x), toMeters(y)), true);
    }

    /** Set body rotation in radians */
    setRotation(handle: number, angle: number): void {
        if (!this.world) return;
        const body = this.world.getRigidBody(handle);
        if (!body) return;
        body.setRotation(angle, true);
    }

    /** Set body linear velocity in pixels/second */
    setVelocity(handle: number, vx: number, vy: number): void {
        if (!this.world) return;
        const body = this.world.getRigidBody(handle);
        if (!body) return;
        body.setLinvel(new RAPIER.Vector2(toMeters(vx), toMeters(vy)), true);
    }

    /** Set body angular velocity in radians/second */
    setAngularVelocity(handle: number, omega: number): void {
        if (!this.world) return;
        const body = this.world.getRigidBody(handle);
        if (!body) return;
        body.setAngvel(omega, true);
    }

    /** Get body angular damping */
    getAngularDamping(handle: number): number | null {
        if (!this.world) return null;
        const body = this.world.getRigidBody(handle);
        if (!body) return null;
        return body.angularDamping();
    }

    /** Set body angular damping (higher = resists rotation more) */
    setAngularDamping(handle: number, damping: number): void {
        if (!this.world) return;
        const body = this.world.getRigidBody(handle);
        if (!body) return;
        body.setAngularDamping(damping);
    }

    /** Apply a torque impulse to a body (instantaneous rotational kick) */
    applyTorqueImpulse(handle: number, torque: number): void {
        if (!this.world) return;
        const body = this.world.getRigidBody(handle);
        if (!body) return;
        body.applyTorqueImpulse(torque, true);
    }

    /** Add a continuous torque to a body (integrated over timestep) */
    addTorque(handle: number, torque: number): void {
        if (!this.world) return;
        const body = this.world.getRigidBody(handle);
        if (!body) return;
        body.addTorque(torque, true);
    }

    /** Lock or unlock rotations for a body */
    lockRotations(handle: number, locked: boolean): void {
        if (!this.world) return;
        const body = this.world.getRigidBody(handle);
        if (!body) return;
        body.lockRotations(locked, true);
    }

    /** Set kinematic target position (for held objects) */
    setKinematicTarget(handle: number, x: number, y: number, angle: number): void {
        if (!this.world) return;
        const body = this.world.getRigidBody(handle);
        if (!body) return;
        body.setNextKinematicTranslation(new RAPIER.Vector2(toMeters(x), toMeters(y)));
        body.setNextKinematicRotation(angle);
    }

    /** Set body type (dynamic, kinematic, static) */
    setBodyType(handle: number, type: BodyType): void {
        if (!this.world) return;
        const body = this.world.getRigidBody(handle);
        if (!body) return;
        
        switch (type) {
            case BodyType.Dynamic:
                body.setBodyType(RAPIER.RigidBodyType.Dynamic, true);
                break;
            case BodyType.Kinematic:
                body.setBodyType(RAPIER.RigidBodyType.KinematicPositionBased, true);
                break;
            case BodyType.Static:
                body.setBodyType(RAPIER.RigidBodyType.Fixed, true);
                break;
        }
    }

    /** 
     * Query for a PACKAGE body at a point (in pixels).
     * Only returns package bodies, not environment bodies.
     */
    queryPoint(x: number, y: number): PhysicsBody | null {
        if (!this.world) return null;

        const point = new RAPIER.Vector2(toMeters(x), toMeters(y));
        let foundBody: PhysicsBody | null = null;

        // Use World's built-in intersectionsWithPoint method
        this.world.intersectionsWithPoint(
            point,
            (collider) => {
                const body = collider.parent();
                if (body) {
                    // Only return package bodies (which are in this.bodies)
                    const physicsBody = this.bodies.get(body.handle);
                    if (physicsBody && physicsBody.userData.category === 'package') {
                        foundBody = physicsBody;
                        return false; // Stop searching
                    }
                }
                return true; // Continue searching
            }
        );

        return foundBody;
    }

    /**
     * Query for the nearest PACKAGE body within a radius of a point (in pixels).
     * Returns the closest package body and the closest point on its surface.
     */
    queryNearestInRadius(x: number, y: number, radius: number): { body: PhysicsBody; closestX: number; closestY: number } | null {
        if (!this.world) return null;

        const point = new RAPIER.Vector2(toMeters(x), toMeters(y));
        let nearestBody: PhysicsBody | null = null;
        let nearestDist = radius;
        let nearestClosestX = x;
        let nearestClosestY = y;

        // Iterate through all package bodies and find the nearest
        for (const physicsBody of this.bodies.values()) {
            if (physicsBody.userData.category !== 'package') continue;

            const body = this.world.getRigidBody(physicsBody.handle);
            if (!body) continue;

            // Check each collider on this body
            for (let i = 0; i < body.numColliders(); i++) {
                const collider = body.collider(i);
                
                // Project point onto collider to find closest point
                const projected = collider.projectPoint(point, true);
                if (!projected) continue;
                
                const closestX = toPixels(projected.point.x);
                const closestY = toPixels(projected.point.y);
                
                // Calculate distance
                const dx = closestX - x;
                const dy = closestY - y;
                const dist = Math.sqrt(dx * dx + dy * dy);
                
                if (dist < nearestDist) {
                    nearestDist = dist;
                    nearestBody = physicsBody;
                    nearestClosestX = closestX;
                    nearestClosestY = closestY;
                }
            }
        }

        if (nearestBody) {
            return { body: nearestBody, closestX: nearestClosestX, closestY: nearestClosestY };
        }
        return null;
    }

    /**
     * Set friction coefficient on all colliders of a body.
     */
    setFriction(handle: number, friction: number): void {
        if (!this.world) return;
        const body = this.world.getRigidBody(handle);
        if (!body) return;

        // Iterate through all colliders attached to this body
        for (let i = 0; i < body.numColliders(); i++) {
            const collider = body.collider(i);
            collider.setFriction(friction);
        }
    }

    /**
     * Get friction coefficient from the first collider of a body.
     */
    getFriction(handle: number): number | null {
        if (!this.world) return null;
        const body = this.world.getRigidBody(handle);
        if (!body || body.numColliders() === 0) return null;

        const collider = body.collider(0);
        return collider.friction();
    }

    /** Remove a body from the world */
    removeBody(handle: number): void {
        if (!this.world) return;
        const body = this.world.getRigidBody(handle);
        if (body) {
            this.world.removeRigidBody(body);
        }
        this.bodies.delete(handle);
    }

    /** Remove a static environment body by handle */
    removeStaticBody(handle: number): void {
        if (!this.world) return;
        const body = this.world.getRigidBody(handle);
        if (body) {
            this.world.removeRigidBody(body);
        }
    }

    /** Get all PACKAGE bodies (not environment bodies) */
    getAllBodies(): PhysicsBody[] {
        return Array.from(this.bodies.values());
    }

    /** Get a specific package body by handle */
    getBody(handle: number): PhysicsBody | undefined {
        return this.bodies.get(handle);
    }

    /** Get the raw Rapier world (for advanced usage) */
    getRawWorld(): RAPIER.World | null {
        return this.world;
    }

    /** Clean up resources */
    destroy(): void {
        this.bodies.clear();
        this.world = null;
    }
}
